import { useState } from 'react';
import { supabase } from './supabase.js';
import { CL } from './colors.js';
import { L } from './lang.js';
import { localEodToUtc } from './format.js';

const TRIAL_PRESETS = [
  { label: L.trial14, value: '14' },
  { label: L.trial30, value: '30' },
  { label: L.trial60, value: '60' },
  { label: L.trial90, value: '90' },
  { label: L.trialCustom, value: 'custom' },
];

const MIN_DATE = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
const MAX_DATE = new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10);

const inputStyle = {
  display: 'block', width: '100%', padding: '8px 10px',
  border: `1px solid ${CL.goldL}`, borderRadius: 5,
  fontFamily: "'Figtree', sans-serif", fontSize: 13,
  background: CL.cream, color: CL.bark,
};

export function NewCustomerForm({ onClose, onCreated }) {
  const [idemKey] = useState(() => crypto.randomUUID());
  const [hotelName, setHotelName]       = useState('');
  const [ownerName, setOwnerName]       = useState('');
  const [ownerEmail, setOwnerEmail]     = useState('');
  const [country, setCountry]           = useState('GR');
  const [trialOption, setTrialOption]   = useState('30');
  const [customDate, setCustomDate]     = useState('');
  const [pending, setPending]           = useState(false);
  const [fieldError, setFieldError]     = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (pending) return;
    setPending(true);
    setFieldError(null);

    const body = trialOption === 'custom'
      ? {
          hotel_name:      hotelName.trim(),
          owner_full_name: ownerName.trim(),
          owner_email:     ownerEmail.trim().toLowerCase(),
          country,
          trial_ends_at:   localEodToUtc(customDate, country),
        }
      : {
          hotel_name:        hotelName.trim(),
          owner_full_name:   ownerName.trim(),
          owner_email:       ownerEmail.trim().toLowerCase(),
          country,
          trial_length_days: parseInt(trialOption, 10),
        };

    const { data: { session } } = await supabase.auth.getSession();
    let res, data;
    try {
      res = await fetch(
        `${import.meta.env.VITE_SUPA_URL}/functions/v1/admin-create-customer`,
        {
          method: 'POST',
          headers: {
            Authorization:       `Bearer ${session.access_token}`,
            'Content-Type':      'application/json',
            'Idempotency-Key':   idemKey,
          },
          body: JSON.stringify(body),
        }
      );
      data = await res.json();
    } catch (err) {
      setPending(false);
      setFieldError({ field: null, message: 'Network error — please try again.' });
      return;
    }

    setPending(false);

    if (!res.ok) {
      setFieldError({ field: data.field ?? null, message: data.message ?? 'An error occurred.' });
      return;
    }

    onCreated(data);
  }

  const errFor = (field) =>
    fieldError && fieldError.field === field ? fieldError.message : null;
  const genericErr = fieldError && !fieldError.field ? fieldError.message : null;

  return (
    <div data-testid="new-customer-form" style={{ background: CL.cream, border: `1px solid ${CL.goldL}`, borderRadius: 10,
                  padding: '20px 24px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: CL.bark }}>
          {L.newCustomerTitle}
        </h2>
        <button onClick={onClose}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                   fontSize: 20, color: CL.stone, lineHeight: 1, padding: '0 4px' }}>
          ×
        </button>
      </div>

      <form onSubmit={submit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12,
                      marginBottom: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600,
                            color: CL.stone, marginBottom: 4 }}>
              {L.newCustomerHotel} *
            </label>
            <input type="text" required value={hotelName} maxLength={120} placeholder="Hotel name"
              onChange={e => setHotelName(e.target.value)} style={inputStyle} />
            {errFor('hotel_name') && <Err msg={errFor('hotel_name')} />}
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600,
                            color: CL.stone, marginBottom: 4 }}>
              {L.newCustomerOwner} *
            </label>
            <input type="text" required value={ownerName} maxLength={120} placeholder="Owner full name"
              onChange={e => setOwnerName(e.target.value)} style={inputStyle} />
            {errFor('owner_full_name') && <Err msg={errFor('owner_full_name')} />}
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600,
                            color: CL.stone, marginBottom: 4 }}>
              {L.newCustomerEmail} *
            </label>
            <input type="email" required value={ownerEmail}
              onChange={e => setOwnerEmail(e.target.value)} style={inputStyle} />
            {errFor('owner_email') && <Err msg={errFor('owner_email')} />}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12,
                      marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600,
                            color: CL.stone, marginBottom: 4 }}>
              {L.newCustomerCountry}
            </label>
            <select value={country} onChange={e => setCountry(e.target.value)}
              style={{ ...inputStyle, appearance: 'auto' }}>
              <option value="GR">{L.countryGR}</option>
              <option value="BG">{L.countryBG}</option>
            </select>
            {errFor('country') && <Err msg={errFor('country')} />}
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600,
                            color: CL.stone, marginBottom: 4 }}>
              {L.newCustomerTrial}
            </label>
            <select value={trialOption} onChange={e => setTrialOption(e.target.value)}
              style={{ ...inputStyle, appearance: 'auto' }}>
              {TRIAL_PRESETS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          {trialOption === 'custom' && (
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600,
                              color: CL.stone, marginBottom: 4 }}>
                Trial end date *
              </label>
              <input type="date" required value={customDate} min={MIN_DATE} max={MAX_DATE}
                onChange={e => setCustomDate(e.target.value)}
                style={inputStyle} />
              {errFor('trial_ends_at') && <Err msg={errFor('trial_ends_at')} />}
            </div>
          )}
        </div>

        {errFor('trial_length_days') && <Err msg={errFor('trial_length_days')} />}
        {genericErr && <Err msg={genericErr} />}

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="submit" disabled={pending}
            style={{ background: pending ? CL.stone : CL.bark, color: CL.cream,
                     border: 'none', padding: '8px 18px', borderRadius: 6,
                     fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                     cursor: pending ? 'not-allowed' : 'pointer', opacity: pending ? 0.6 : 1 }}>
            {pending ? 'Creating…' : L.newCustomerSubmit}
          </button>
          <button type="button" onClick={onClose}
            style={{ background: 'transparent', border: `1px solid ${CL.goldL}`, color: CL.bark,
                     padding: '8px 14px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit',
                     cursor: 'pointer' }}>
            {L.newCustomerCancel}
          </button>
        </div>
      </form>
    </div>
  );
}

function Err({ msg }) {
  return <p style={{ color: CL.terra, fontSize: 12, marginTop: 4 }}>{msg}</p>;
}
